import { API_BASE_URL } from '../config/api';

export const TUKAR_POIN_COLORS = {
  navy: '#1E293B',
  gold: '#F1C16E',
  goldMid: '#E8A317',
  goldDark: '#D38A0F',
  price: '#E28B16',
  promo: '#E11D48',
  promoDeep: '#BE123C',
  fisikBg: '#DBEAFE',
  fisikText: '#1D4ED8',
  success: '#047857',
  successBg: '#D1FAE5',
  error: '#DC2626',
  errorBg: '#FEE2E2',
  muted: '#94A3B8',
  slate: '#64748B',
  chip: '#EEF0F3',
  surface: '#F8FAFC',
};

const MONTH_SHORT_ID = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

const parseDateValue = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
    const [d, m, y] = raw.slice(0, 10).split('-');
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.slice(0, 10).split('-');
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateShort = (date) =>
  `${date.getDate()} ${MONTH_SHORT_ID[date.getMonth()]} ${date.getFullYear()}`;

export const formatPoinNumber = (value) =>
  (Number(value) || 0).toLocaleString('id-ID');

export const formatPoin = (value) => `${formatPoinNumber(value)} pt`;

export const formatRp = (value) => {
  const n = Number(value) || 0;
  return `Rp ${n.toLocaleString('id-ID')}`;
};

export const formatDateId = (value) => {
  const date = parseDateValue(value);
  if (!date) return value ? String(value) : '';
  return formatDateShort(date);
};

export const formatDateRange = (start, end) => {
  const from = parseDateValue(start);
  const to = parseDateValue(end);
  if (!from && !to) return '';
  if (from && to) {
    return `${formatDateShort(from)} – ${formatDateShort(to)}`;
  }
  return formatDateShort(from || to);
};

export const paymentUrl = (item) =>
  item?.xendit_payment_url || item?.payment_url || '';

export const linkajaQrUrl = (item) => {
  const path = item?.linkaja_qr_url || item?.linkaja_qr_path || '';
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const base = String(API_BASE_URL || '').replace(/\/$/, '');
  return `${base}/${String(path).replace(/^\//, '')}`;
};

export const hasUnpaidOngkir = (item) =>
  String(item?.status_redeem || '') === 'menunggu_bayar_ongkir'
  && !!(paymentUrl(item) || linkajaQrUrl(item));

export const historyOrderPath = (item) => {
  const target = String(item?.transaksi_url || '').trim();
  if (!target) return '/order';
  if (/^https?:\/\//i.test(target)) {
    try {
      return new URL(target).pathname || '/order';
    } catch (e) {
      return '/order';
    }
  }
  return target.startsWith('/') ? target : `/${target}`;
};

export const formatDateTimeId = (value) => {
  const date = parseDateValue(value);
  if (!date) return value ? String(value) : '';
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${formatDateShort(date)} ${hh}:${mm}`;
};

export const paymentExpireHint = (item) => {
  if (item?.payment_expires_at) {
    return `Bayar sebelum ${formatDateTimeId(item.payment_expires_at)}.`;
  }
  const sec = Number(item?.payment_expire_seconds || 0);
  if (sec >= 86400 && sec % 86400 === 0) {
    const days = sec / 86400;
    return `Selesaikan pembayaran dalam ${days} hari.`;
  }
  if (sec >= 3600) {
    const hours = Math.round(sec / 3600);
    return `Selesaikan pembayaran dalam ${hours} jam.`;
  }
  if (sec > 0) {
    const mins = Math.max(1, Math.round(sec / 60));
    return `Selesaikan pembayaran dalam ${mins} menit.`;
  }
  return '';
};

export const paymentExpiredAtTs = (item) => {
  if( !!item?.payment_expires_at ) {
    const expiredDate = new Date(item?.payment_expires_at);
    const expiredTs = expiredDate.getTime();
    if( isNaN( expiredTs ) ) {
      return null;
    } else {
      return expiredTs;
    }
  }

  return null;
};

export const isVoucherReward = (item) =>
  String(item?.kategori || '').toLowerCase() === 'voucher';

export const isLinkAjaReward = (item) => {
  if (!item) return false;
  if (item.is_linkaja) return true;
  const kategori = String(item.product_kategori || item.kategori || '').toLowerCase();
  if (kategori === 'linkaja' || kategori.includes('link aja') || kategori.includes('linkaja')) {
    return true;
  }
  return /link\s*aja/i.test(String(item.nama_reward || ''));
};

export const isPhysicalHistoryItem = (item) =>
  String(item?.tipe_item || '').toLowerCase() === 'fisik';

export const shopOrderUrl = () => '/order';

export const needsShipping = (item) =>
  !!(item?.perlu_pengiriman || (!isVoucherReward(item) && item?.kategori));

export const activePoin = (item) =>
  Number(item?.harga_poin_aktif ?? item?.harga_poin ?? 0);

export const isPromoConfigured = (item) => {
  if (String(item?.status_promo || '').toLowerCase() !== 'aktif') return false;
  return !!(item?.promo_tanggal_mulai && item?.promo_tanggal_berakhir);
};

const isNowInRange = (startValue, endValue) => {
  const start = parseDateValue(startValue);
  const end = parseDateValue(endValue);
  const now = Date.now();
  if (start && now < start.getTime()) return false;
  if (end) {
    // 00:00:00 end = end of that calendar day (same as PHP).
    const endTs = end.getHours() === 0 && end.getMinutes() === 0 && end.getSeconds() === 0
      ? end.getTime() + 86399999
      : end.getTime();
    if (now > endTs) return false;
  }
  return true;
};

export const isInPromo = (item) => {
  if (typeof item?.is_in_promo === 'boolean') {
    return item.is_in_promo;
  }
  if (!isPromoConfigured(item)) return false;
  return isNowInRange(item.promo_tanggal_mulai, item.promo_tanggal_berakhir);
};

export const catalogPoin = (item) => {
  const skus = rewardSkus(item);
  if (skus.length > 0) {
    const vals = skus
      .map((sku) => skuActivePoin(item, sku))
      .filter((p) => p > 0);
    if (vals.length) return Math.min(...vals);
  }
  const aktif = Number(item?.harga_poin_aktif ?? 0);
  if (aktif > 0) return aktif;
  const normal = Number(item?.harga_poin ?? 0);
  const promoHarga = Number(item?.promo_harga_poin ?? 0);
  if (isInPromo(item) && promoHarga > 0 && (normal < 1 || promoHarga < normal)) {
    return promoHarga;
  }
  return activePoin(item);
};

export const catalogStrikePoin = (item) => {
  const normal = Number(item?.harga_poin ?? 0);
  const aktif = catalogPoin(item);
  if (!(item?.is_in_promo || isInPromo(item))) return 0;
  if (normal > 0 && aktif > 0 && aktif < normal) return normal;
  return 0;
};

export const hasEnoughPoin = (item, saldo) => catalogPoin(item) <= Number(saldo || 0);

export const matchesExchangable = (item, saldo) => {
  if (String(item?.status || '') !== 'aktif') return false;
  if (!isPeriodActive(item)) return false;
  if (!isInStock(item)) return false;
  if (claimLimitState(item).limitReached) return false;
  return hasEnoughPoin(item, saldo);
};

export const isPeriodActive = (item) => {
  if (!item) return false;
  return isNowInRange(item.tanggal_mulai, item.tanggal_berakhir);
};

export const plainRewardText = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+/g, ' ')
    .trim();
};

export const rewardSkus = (item) =>
  Array.isArray(item?.variants) ? item.variants : [];

export const findRewardSku = (item, skuId) =>
  rewardSkus(item).find((sku) => Number(sku.id) === Number(skuId)) || null;

export const skuInStock = (sku) => !sku || sku.in_stock !== false || !!sku.stok_tanpa_batas;

export const firstInStockSku = (item) =>
  rewardSkus(item).find((sku) => skuInStock(sku)) || rewardSkus(item)[0] || null;

export const needsSkuPick = (item) => rewardSkus(item).length > 1;

export const skuVarianGroups = (item) => {
  const map = {};
  rewardSkus(item).forEach((sku) => {
    const vid = String(sku.id_varian || 0);
    if (!map[vid]) {
      map[vid] = { id: vid, nama: sku.varian || 'Default' };
    }
  });
  return Object.values(map);
};

export const skuActivePoin = (item, sku) => {
  if (!sku) return catalogPoin(item);
  const promoOn = !!(item?.is_in_promo || isInPromo(item));
  const promo = Number(sku.harga_poin_promo || 0);
  const normal = Number(sku.harga_poin || 0);
  if (promoOn && promo > 0 && (normal < 1 || promo < normal)) return promo;
  return normal;
};

export const skuImageUrl = (item, sku) =>
  String(sku?.gambar_url || item?.gambar_url || '').trim();

export const isInStock = (item, sku) => {
  if (!sku && typeof item?.in_stock === 'boolean') {
    return item.in_stock;
  }
  if ((item?.is_in_promo || isInPromo(item)) && item?.promo_qty != null && Number(item.promo_qty) > 0) {
    if (item?.promo_qty_tersedia != null && Number(item.promo_qty_tersedia) < 1) {
      return false;
    }
  }
  if (sku) return skuInStock(sku);
  const skus = rewardSkus(item);
  if (skus.length) return skus.some((row) => skuInStock(row));
  if (item?.stok_tanpa_batas) return true;
  if (item?.stok_tersedia == null) return true;
  return Number(item.stok_tersedia) > 0;
};

const skuStockQty = (sku) => {
  if (!sku) return null;
  if (sku.stok_tanpa_batas) return null;
  if (sku.qty_stok != null && sku.qty_stok !== '') return Math.max(0, Number(sku.qty_stok));
  if (sku.stok_tersedia != null && sku.stok_tersedia !== '') return Math.max(0, Number(sku.stok_tersedia));
  return null;
};

export const stockLabel = (item, sku = null) => {
  if (!item) return null;
  const promoOn = !!(item?.is_in_promo || isInPromo(item));
  if (!promoOn) return null;

  const hasPromoQty = item?.promo_qty != null && Number(item.promo_qty) > 0;
  const promoLeft = hasPromoQty
    ? Math.max(0, Number(item.promo_qty_tersedia != null ? item.promo_qty_tersedia : item.promo_qty))
    : null;

  let unlimited = false;
  let productQty = null;
  if (sku) {
    if (sku.stok_tanpa_batas) unlimited = true;
    else productQty = skuStockQty(sku);
  } else {
    const skus = rewardSkus(item);
    if (skus.length) {
      if (skus.some((row) => row.stok_tanpa_batas)) unlimited = true;
      else {
        productQty = skus.reduce((sum, row) => {
          const qty = skuStockQty(row);
          return sum + (qty != null ? qty : 0);
        }, 0);
      }
    } else if (item?.stok_tanpa_batas) {
      unlimited = true;
    } else if (item?.stok_tersedia != null && item?.stok_tersedia !== '') {
      productQty = Math.max(0, Number(item.stok_tersedia));
    }
  }

  if (hasPromoQty && promoLeft === 0) return 'Sisa: 0';
  if (!unlimited && productQty != null && productQty < 1) return 'Sisa: 0';
  if (hasPromoQty) {
    if (!unlimited && productQty != null) {
      return `Sisa: ${Math.min(promoLeft, productQty)}`;
    }
    return `Sisa: ${promoLeft}`;
  }
  if (unlimited) return 'Sisa: tanpa batas';
  if (productQty != null) return `Sisa: ${productQty}`;
  return null;
};

export const canClaim = (item, saldo) =>
  String(item?.status || '') === 'aktif'
  && isPeriodActive(item)
  && isInStock(item)
  && !claimLimitState(item).limitReached
  && hasEnoughPoin(item, saldo);

export const poinShort = (item, saldo) =>
  Math.max(0, activePoin(item) - Number(saldo || 0));

export const catalogTabs = [
  { key: 'promo', label: 'Hot Deals', icon: 'chili' },
  { key: 'bisa_klaim', label: 'Bisa Klaim' },
  { key: 'reward_lainnya', label: 'Reward Lainnya' },
];

export const matchesCatalogTab = (item, tab, saldo) => {
  if (tab === 'promo') {
    if (typeof item?.is_in_promo === 'boolean') return item.is_in_promo;
    return isInPromo(item);
  }
  if (tab === 'reward_lainnya') return true;
  return matchesExchangable(item, saldo);
};

export const buildCatalogChips = (items = []) => {
  const chips = ['Semua'];
  const seen = new Set(['Semua']);
  (items || []).forEach((item) => {
    const label = isVoucherReward(item)
      ? 'Voucher'
      : String(item?.product_kategori || '').trim();
    if (!label || label === 'Hadiah Fisik' || seen.has(label)) return;
    seen.add(label);
    chips.push(label);
  });
  return chips;
};

export const catalogItemCategory = (item) => {
  if (isVoucherReward(item)) return 'Voucher';
  return String(item?.product_kategori || '').trim();
};

export const matchesCatalogChip = (item, chip) => {
  if (!chip || chip === 'Semua') return true;
  return catalogItemCategory(item) === chip;
};

export const categoryBadge = (item) => {
  if (isVoucherReward(item)) {
    return { label: 'Voucher', bg: '#FEF3C7', color: '#B45309' };
  }
  const kategori = String(item?.product_kategori || '').trim();
  return {
    label: kategori,
    bg: '#DBEAFE',
    color: '#3B82F6',
  };
};

export const catalogTags = (item) => {
  const tags = [];
  const push = (value) => {
    const label = String(value || '').trim();
    if (label && !tags.includes(label)) tags.push(label);
  };
  if (isVoucherReward(item)) {
    return [];
  }
  const kategori = String(item?.product_kategori || '').trim();
  const brand = String(item?.product_brand || '').trim();
  if (kategori && brand) {
    push(`${kategori} ${brand}`);
  } else {
    push(kategori);
  }
  push(brand);
  if (!item?.poin_mengikuti_sku && rewardSkus(item).length < 2) {
    const size = item?.product_ukuran || item?.product_varian;
    if (size) push(`Size: ${size}`);
  }
  return tags.slice(0, 3);
};

export const isRejectedExchange = (item) => {
  if (!item || item.status_redeem !== 'dibatalkan') return false;
  const catatan = String(item.catatan || item.catatan_admin || '');
  if (catatan.startsWith('By customer')) return false;
  const tipe = String(item.tipe_item || '');
  if (tipe === 'voucher') return true;
  return tipe === 'fisik' && !!item.ongkir_paid_at;
};

export const statusMeta = (status, item) => {
  const map = {
    menunggu_bayar_ongkir: { label: 'Bayar ongkir', color: '#D97706', bg: '#FEF3C7' },
    pending: { label: 'Diproses', color: '#06B6D4', bg: '#CFFAFE' },
    diproses: { label: 'Diproses', color: '#06B6D4', bg: '#EDE9FE' },
    dikirim: { label: 'Dikirim', color: '#0369A1', bg: '#E0F2FE' },
    selesai: { label: 'Berhasil', color: '#00C16A', bg: TUKAR_POIN_COLORS.successBg },
    dibatalkan: {
      label: isRejectedExchange({ ...(item || {}), status_redeem: status }) ? 'Ditolak' : 'Dibatalkan',
      color: '#F43F5E',
      bg: TUKAR_POIN_COLORS.errorBg,
    },
  };
  return map[status] || { label: status || 'Status', color: '#475569', bg: '#E2E8F0' };
};

export const processingSlaCopy = (item) => {
  const status = String(item?.status_redeem || '');
  if (status !== 'pending' && status !== 'diproses') return '';
  if (String(item?.tipe_item || '').toLowerCase() === 'voucher') {
    return 'Diproses admin maksimal 3 hari kerja. Voucher masuk ke Shopping Coupon.';
  }
  return 'Pengiriman diproses maksimal 7 hari kerja setelah ongkir dibayar.';
};

export const successCopy = (claimData, isVoucher) => {
  const status = claimData?.status_redeem;
  if (status === 'menunggu_bayar_ongkir') {
    const via = linkajaQrUrl(claimData) ? 'QRIS' : 'Xendit';
    const expiry = paymentExpireHint(claimData);
    return {
      title: 'Bayar ongkir dulu',
      body: `Selesaikan pembayaran via ${via} untuk melanjutkan klaim.${expiry ? ` ${expiry}` : ''}`,
    };
  }
  if (isVoucher) {
    return {
      title: 'Penukaran Berhasil',
      body: 'Admin akan meninjau klaim ini. Diproses admin maksimal 3 hari kerja. Voucher masuk ke Shopping Coupon.',
    };
  }
  return {
    title: 'Penukaran Berhasil',
    body: 'Ongkir sudah lunas. Pengiriman diproses maksimal 7 hari kerja setelah ongkir dibayar.',
  };
};

export const addressLine = (item) =>
  [item?.detail_alamat, item?.kecamatan, item?.kota, item?.provinsi, item?.kodepos]
    .filter(Boolean)
    .join(', ');

export const claimLimitState = (item) => {
  const maxRedeem = Number(item?.max_redeem_per_user || 0);
  const usedRedeem = Number(item?.user_redeem_count || 0);
  let maxCoupon = Number(item?.coupon_maksimal_per_user || 0);
  if (isVoucherReward(item) && maxCoupon < 1) {
    maxCoupon = 1;
  }
  const usedCoupon = Number(item?.user_coupon_claim_count || 0);
  const maxPromo = Number(item?.promo_max_redeem_per_user || 0);
  const usedPromo = Number(item?.user_promo_redeem_count || 0);
  const promoQtyLeft = item?.promo_qty_tersedia;
  const promoOn = !!(item?.is_in_promo || isInPromo(item));
  const promoUserLimitActive = promoOn && maxPromo > 0;
  const rewardLimitReached = !promoUserLimitActive && maxRedeem > 0 && usedRedeem >= maxRedeem;
  const couponLimitReached = maxCoupon > 0 && usedCoupon >= maxCoupon;
  const promoUserLimitReached = promoUserLimitActive && usedPromo >= maxPromo;
  const promoQtyReached = promoOn && promoQtyLeft != null && Number(promoQtyLeft) < 1;
  return {
    rewardLimitReached,
    couponLimitReached,
    promoUserLimitReached,
    promoQtyReached,
    limitReached: rewardLimitReached || couponLimitReached || promoUserLimitReached || promoQtyReached,
  };
};

export const claimLimitCopy = (item) => {
  const { couponLimitReached, rewardLimitReached, promoUserLimitReached, promoQtyReached } = claimLimitState(item);
  if (promoQtyReached) {
    return {
      title: 'Kuota Promo Habis',
      body: 'Kuota promo reward ini sudah habis. Silakan pilih reward lain di katalog.',
    };
  }
  if (promoUserLimitReached && !rewardLimitReached) {
    return {
      title: 'Batas Promo Tercapai',
      body: 'Kamu sudah menukar reward ini sesuai batas promo yang berlaku. Silakan pilih reward lain di katalog.',
    };
  }
  if (couponLimitReached && !rewardLimitReached) {
    return {
      title: 'Batas Kupon Tercapai',
      body: 'Kamu sudah klaim kupon sumber reward ini sesuai batas yang berlaku. Silakan pilih reward lain di katalog.',
    };
  }
  return {
    title: 'Batas Klaim Tercapai',
    body: 'Kamu sudah menukar reward ini sesuai batas yang berlaku. Silakan pilih reward lain di katalog.',
  };
};

export const claimWarnCopy = (item, {
  enoughPoints = true,
  kurangPoin = 0,
  inStock = true,
  periodActive = true,
} = {}) => {
  const limits = claimLimitState(item);
  if (!periodActive) {
    return {
      title: 'Reward Tidak Tersedia',
      body: 'Periode tukar poin sudah berakhir atau belum dimulai.',
    };
  }
  if (limits.promoQtyReached) {
    return claimLimitCopy(item);
  }
  if (!inStock) {
    return {
      title: 'Stok Habis',
      body: 'Reward ini sedang tidak tersedia. Silakan pilih reward lain di katalog.',
    };
  }
  if (!enoughPoints) {
    return {
      title: 'Syarat Penukaran Belum Terpenuhi',
      body: `Poinmu kurang ${formatPoinNumber(kurangPoin)} poin. Ayo terus belanja, atau dapatkan poin melalui Spinning Wheel di Zoolyfe-Club, atau promo-promo poin lainnya.`,
    };
  }
  if (limits.limitReached) {
    return claimLimitCopy(item);
  }
  return null;
};

export const claimInfoCopy = (raw) => {
  const msg = String(raw || '').trim();
  const lower = msg.toLowerCase();
  if (lower.includes('batas klaim kupon') || (lower.includes('kupon') && lower.includes('sudah tercapai'))) {
    return {
      title: 'Batas Kupon Tercapai',
      body: 'Kamu sudah klaim kupon sumber reward ini sesuai batas yang berlaku. Silakan pilih reward lain di katalog.',
    };
  }
  if (lower.includes('kuota promo')) {
    return {
      title: 'Kuota Promo Habis',
      body: 'Kuota promo reward ini sudah habis. Silakan pilih reward lain di katalog.',
    };
  }
  if (lower.includes('batas penukaran promo') || lower.includes('batas promo')) {
    return {
      title: 'Batas Promo Tercapai',
      body: 'Kamu sudah menukar reward ini sesuai batas promo yang berlaku. Silakan pilih reward lain di katalog.',
    };
  }
  if (lower.includes('batas penukaran') || lower.includes('sudah tercapai')) {
    return {
      title: 'Batas Klaim Tercapai',
      body: 'Kamu sudah menukar reward ini sesuai batas yang berlaku. Silakan pilih reward lain di katalog.',
    };
  }
  if (lower.includes('stok')) {
    return {
      title: 'Stok Habis',
      body: 'Reward ini sedang tidak tersedia. Silakan pilih reward lain di katalog.',
    };
  }
  if (lower.includes('poin tidak mencukupi') || lower.includes('saldo poin tidak cukup') || lower.includes('poin kurang')) {
    return {
      title: 'Poin Tidak Cukup',
      body: msg || 'Poin kamu belum cukup untuk menukar reward ini.',
    };
  }
  if (lower.includes('belum aktif')) {
    return {
      title: 'Belum Dapat Diklaim',
      body: 'Periode tukar poin untuk reward ini belum dimulai.',
    };
  }
  if (lower.includes('sudah berakhir') || lower.includes('tidak aktif')) {
    return {
      title: 'Reward Tidak Tersedia',
      body: 'Reward ini tidak aktif atau periodenya sudah berakhir.',
    };
  }
  if (lower.includes('alamat')) {
    return {
      title: 'Alamat Diperlukan',
      body: 'Lengkapi alamat pengiriman untuk menukar hadiah fisik.',
    };
  }
  if (lower.includes('kurir') || (lower.includes('ongkir') && lower.includes('gagal'))) {
    return {
      title: 'Kurir Belum Tersedia',
      body: msg || 'Tarif pengiriman belum bisa ditampilkan. Coba pilih alamat lain atau ulangi beberapa saat lagi.',
    };
  }
  return {
    title: 'Klaim Belum Bisa Diproses',
    body: msg || 'Terjadi kendala saat klaim. Silakan coba lagi.',
  };
};
